import moment from "moment";
import { BaseEntity, getCachedRegionEntity } from ".";
import { IGet_markets_region_id_orders_200_ok } from "../service/esiclient";
import { getCachedTypeEntity } from "./TypeEntityCache";

export class MarketOrderStatsEntity extends BaseEntity {
  private _regionID: number;
  private _time: any;
  private _location: any;
  constructor(id: number, regionid: number, props: any, time: any, location: any) {
    super(id, props);
    this._regionID = regionid;
    this._time = time;
    this._location = location;
    this._dataTypeName = "Market Orders Stats";
  }
  calculatePercentile(orders: IGet_markets_region_id_orders_200_ok[], percentile: number): number {
    // Calculate the  percentile price based on volume
    const totalVolume = orders.reduce((sum, order) => sum + order.volume_remain, 0);
    const percentileVolume = (percentile / 100) * totalVolume;

    let cumulativeVolume = 0;
    let percentilePrice = 0;

    for (const order of orders) {
      cumulativeVolume += order.volume_remain;

      if (cumulativeVolume >= percentileVolume) {
        percentilePrice = order.price;
        break;
      }
    }

    return percentilePrice;
  }

  calculateMedian(numbers: number[]): number {
    const sortedNumbers = [...numbers].sort((a, b) => a - b);
    const middle = Math.floor(sortedNumbers.length / 2);
    if (sortedNumbers.length % 2 === 0) {
      return (sortedNumbers[middle - 1] + sortedNumbers[middle]) / 2;
    } else {
      return sortedNumbers[middle];
    }
  }

  async toJson(): Promise<Excel.EntityCellValue> {
    const id = this._id;
    const regionid = this._regionID;
    const props: Partial<IGet_markets_region_id_orders_200_ok[]> = this.props;

    var entity = await super.toJson();

    // Properties
    if (entity.properties) {
      const typeEntity = await getCachedTypeEntity(id);
      const regionEntity = await getCachedRegionEntity(regionid);
      entity.properties["region_id"] = {
        type: Excel.CellValueType.formattedNumber,
        basicValue: regionid,
        numberFormat: "##0",
      };
      entity.properties["type_id"] = {
        type: Excel.CellValueType.formattedNumber,
        basicValue: id,
        numberFormat: "##0",
      };
      if (typeEntity !== null && regionEntity !== null) {
        const entityName = typeEntity.props.name;
        const regionName = regionEntity.props.name.en;
        entity.text = "Statistics for " + entityName + " in " + regionName;
      } else {
        entity.text = "Statistics for " + id + " in " + regionid;
      }
      if (this._location !== null) {
        entity.properties["location_id"] = {
          type: Excel.CellValueType.formattedNumber,
          basicValue: parseInt(this._location),
          numberFormat: "##0",
        };
      }
      if (this._time !== null) {
        const updated = moment(this._time);
        entity.properties["data_updated"] = {
          type: Excel.CellValueType.formattedNumber,
          basicValue: updated.unix() / 86400 + 25569,
          numberFormat: "[$-409]m/d/yy h:mm AM/PM;@",
        };
      }
      delete entity.properties["id"];
      if (props === undefined || props.length === 0) {
        console.log("No orders available.");
      } else {
        const definedOrders: any = props.filter((order) => order !== undefined && order.type_id !== undefined);

        const sellOrders: any = definedOrders
          .filter((order: IGet_markets_region_id_orders_200_ok) => !order.is_buy_order)
          .sort(
            (a: IGet_markets_region_id_orders_200_ok, b: IGet_markets_region_id_orders_200_ok) => b.price - a.price
          );
        const buyOrders: any = definedOrders
          .filter((order: IGet_markets_region_id_orders_200_ok) => order.is_buy_order)
          .sort(
            (a: IGet_markets_region_id_orders_200_ok, b: IGet_markets_region_id_orders_200_ok) => a.price - b.price
          );

        // Assuming you have an array of prices for sellOrders and buyOrders
        const sellPrices: number[] = sellOrders.map((order: any) => order.price || 0); // Extract prices, handle undefined or falsy values
        const buyPrices: number[] = buyOrders.map((order: any) => order.price || 0); // Extract prices, handle undefined or falsy values
        //Min and Max
        const maxvalSell: number = Math.max(...sellPrices);
        const minvalSell: number = Math.min(...sellPrices);

        const maxvalBuy: number = Math.max(...buyPrices);
        const minvalBuy: number = Math.min(...buyPrices);
        // Calculate "percentile" for sell and buy orders, after taking out outliers
        const filteredSellOrders = sellOrders.filter((order: any) => order.price <= minvalSell * 100);
        const filteredBuyOrders = buyOrders.filter((order: any) => order.price >= maxvalBuy / 100);
        const percentileSell = this.calculatePercentile(filteredSellOrders, 95);
        const percentileBuy = this.calculatePercentile(filteredBuyOrders, 95);
        //Volume without outliers
        const sellVolumes: number[] = filteredSellOrders.map((order: any) => order.volume_remain || 0);
        const buyVolumes: number[] = filteredBuyOrders.map((order: any) => order.volume_remain || 0);
        //SellOrders stats
        const volumeSell: number = sellVolumes.reduce((sum, price) => sum + price, 0);
        //These variables still contain the outliers
        const meanSell: number = sellPrices.reduce((sum, price) => sum + price, 0) / sellPrices.length;
        const varianceSell: number =
          sellPrices.reduce((variance, price) => variance + Math.pow(price - meanSell, 2), 0) / sellPrices.length;
        const stddevSell: number = Math.sqrt(varianceSell);
        const medianSell: number = this.calculateMedian(sellPrices);
        //BuyOrders stats
        const volumeBuy: number = buyVolumes.reduce((sum, price) => sum + price, 0);
        //These variables still contain the outliers
        const meanBuy: number = buyPrices.reduce((sum, price) => sum + price, 0) / buyPrices.length;
        const varianceBuy: number =
          buyPrices.reduce((variance, price) => variance + Math.pow(price - meanBuy, 2), 0) / buyPrices.length;
        const stddevBuy: number = Math.sqrt(varianceBuy);
        const medianBuy: number = this.calculateMedian(buyPrices);

        // Aggregation calculations (customize as needed)
        const sellAggregation = {
          max: maxvalSell,
          min: minvalSell,
          stddev: stddevSell,
          median: medianSell,
          volume: volumeSell,
          numorders: sellOrders.length,
          percentile: percentileSell,
        };

        const buyAggregation = {
          max: maxvalBuy,
          min: minvalBuy,
          stddev: stddevBuy,
          median: medianBuy,
          volume: volumeBuy,
          numorders: buyOrders.length,
          percentile: percentileBuy,
        };

        // Display or use the final aggregations as needed
        //console.log("Sell Aggregation:", sellAggregation);
        //console.log("Buy Aggregation:", buyAggregation);
        const buy_entity: Excel.EntityCellValue = {
          type: Excel.CellValueType.entity,
          text: `Buy order statistics`,
          properties: {
            max: {
              type: Excel.CellValueType.double,
              basicValue: buyAggregation.max,
            },
            min: {
              type: Excel.CellValueType.double,
              basicValue: buyAggregation.min,
            },
            stddev: {
              type: Excel.CellValueType.double,
              basicValue: buyAggregation.stddev,
            },
            median: {
              type: Excel.CellValueType.double,
              basicValue: buyAggregation.median,
            },
            volume: {
              type: Excel.CellValueType.double,
              basicValue: buyAggregation.volume,
            },
            numorders: {
              type: Excel.CellValueType.double,
              basicValue: buyAggregation.numorders,
            },
            percentile: {
              type: Excel.CellValueType.double,
              basicValue: buyAggregation.percentile,
            },
            description: {
              type: Excel.CellValueType.string,
              basicValue:
                "Percentile is calculated by removing outliers from the volume calculations, (highest buy / 100), (lowest sell * 100) then finding the first order price that is equal or greater than the 5% of the total available volume.",
            },
          },
        };
        if (buyOrders.length) {
          entity.properties["buy"] = buy_entity;
        }
        const sell_entity: Excel.EntityCellValue = {
          type: Excel.CellValueType.entity,
          text: `Sell order statistics`,
          properties: {
            max: {
              type: Excel.CellValueType.double,
              basicValue: sellAggregation.max,
            },
            min: {
              type: Excel.CellValueType.double,
              basicValue: sellAggregation.min,
            },
            stddev: {
              type: Excel.CellValueType.double,
              basicValue: sellAggregation.stddev,
            },
            median: {
              type: Excel.CellValueType.double,
              basicValue: sellAggregation.median,
            },
            volume: {
              type: Excel.CellValueType.double,
              basicValue: sellAggregation.volume,
            },
            numorders: {
              type: Excel.CellValueType.double,
              basicValue: sellAggregation.numorders,
            },
            percentile: {
              type: Excel.CellValueType.double,
              basicValue: sellAggregation.percentile,
            },
            description: {
              type: Excel.CellValueType.string,
              basicValue:
                "Percentile is calculated by removing outliers from the orderset, (highest buy / 100), (lowest sell * 100), then finding the first order price that is equal or greater than the 5% of the total available volume.",
            },
          },
        };
        if (sellOrders.length) {
          entity.properties["sell"] = sell_entity;
        }
      }
    }

    // Layouts
    entity.layouts = {
      card: {
        title: entity.text,
      },
      compact: {
        icon: "Money",
      },
    };

    return entity;
  }
}
